import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SearchDropdown from './SearchDropdown';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    setShowSearchDropdown(value.trim().length >= 2);
  };

  const handleCloseDropdown = () => {
    setShowSearchDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', type: 'link' },
    { label: 'Categories', href: '/categories', type: 'link' },
    { label: 'Directory', href: '/#tools-directory', type: 'scroll' },
    { label: 'Blog', href: '/blog', type: 'link' },
  ];

  const navLinkClass =
    'relative px-3 py-1.5 text-gray-400 hover:text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap rounded-md hover:bg-white/5 group';
  const underlineClass =
    'absolute bottom-0 left-3 right-3 h-px bg-teal-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full';

  return (
    <nav className="relative z-10 bg-gray-950 border-b border-gray-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Desktop Header ── */}
        <div className="hidden md:grid grid-cols-[1fr_auto_minmax(240px,290px)] items-center h-[180px] gap-4">

          {/* LEFT — Nav links, right-aligned so they sit close to the logo */}
          <div className="flex items-center justify-end gap-0.5">
            {navLinks.map((link) => {
              if (link.type === 'scroll') {
                return (
                  <button
                    key={link.label}
                    onClick={() => {
                      const sectionId = link.href.substring(2);
                      const section = document.getElementById(sectionId);
                      if (section) {
                        section.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate('/');
                        setTimeout(() => {
                          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }
                    }}
                    className={navLinkClass}
                  >
                    {link.label}
                    <span className={underlineClass}></span>
                  </button>
                );
              }
              return (
                <Link key={link.label} to={link.href} className={navLinkClass}>
                  {link.label}
                  <span className={underlineClass}></span>
                </Link>
              );
            })}

            {/* Compare — subtle, integrated */}
            <Link
              to="/compare"
              className="relative flex items-center gap-1.5 px-3 py-1.5 text-teal-400/80 hover:text-teal-300 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap rounded-md hover:bg-teal-500/8 group"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-scales-3-line text-sm"></i>
              </span>
              Compare
              <span className="absolute bottom-0 left-3 right-3 h-px bg-teal-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></span>
            </Link>

            {/* Submit Tool — prominent CTA */}
            <Link
              to="/submit-tool"
              className="flex items-center gap-1.5 px-3 py-1.5 ml-1 text-xs font-semibold text-teal-300 hover:text-white bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/35 hover:border-teal-400/70 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-add-circle-line"></i>
              </span>
              Submit Tool
            </Link>
          </div>

          {/* CENTER — Logo */}
          <div className="flex justify-center items-center">
            <Link to="/" className="flex items-center group cursor-pointer select-none">
              <img
                src="https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png"
                alt="Toolnova"
                className="h-60 w-auto object-contain transition-all duration-300 opacity-95 group-hover:opacity-100 group-hover:drop-shadow-[0_0_8px_rgba(45,212,191,0.5)]"
              />
            </Link>
          </div>

          {/* RIGHT — Search only */}
          <div className="flex items-center">
            <div className="relative w-full" ref={searchRef}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    setShowSearchDropdown(false);
                    setSearchQuery('');
                  }
                }}
              >
                <div className="flex items-center bg-gray-900/70 border border-gray-700/50 rounded-xl overflow-hidden focus-within:border-teal-500/60 focus-within:ring-2 focus-within:ring-teal-500/15 transition-all duration-300">
                  <span className="w-5 h-5 flex items-center justify-center ml-3.5 flex-shrink-0">
                    <i className="ri-search-line text-gray-500 text-base"></i>
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    placeholder="Search AI tools…"
                    className="flex-1 min-w-0 px-2.5 py-2.5 bg-transparent text-sm text-gray-200 placeholder-gray-500 focus:outline-none cursor-text"
                  />
                  <button
                    type="submit"
                    className="flex-shrink-0 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Search
                  </button>
                </div>
              </form>
              <SearchDropdown
                query={searchQuery}
                onClose={handleCloseDropdown}
                isOpen={showSearchDropdown}
              />
            </div>
          </div>

        </div>

        {/* ── Mobile header ── */}
        <div className="md:hidden flex items-center justify-between h-16">
          <Link to="/" className="flex items-center cursor-pointer select-none">
            <img
              src="https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png"
              alt="Toolnova"
              className="h-10 w-auto object-contain"
            />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-teal-400 transition-colors duration-200 cursor-pointer rounded-lg hover:bg-white/5"
            aria-label="Toggle mobile menu"
          >
            <i
              className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-90' : ''}`}
            ></i>
          </button>
        </div>

        {/* ── Mobile Menu Dropdown ── */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 space-y-1 border-t border-gray-800/60">
            <div className="px-4 mb-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    setShowSearchDropdown(false);
                    setSearchQuery('');
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                <div className="flex items-center bg-gray-900/70 border border-gray-700/50 rounded-lg overflow-hidden focus-within:border-teal-500/50 transition-all duration-200">
                  <span className="w-5 h-5 flex items-center justify-center ml-3 flex-shrink-0">
                    <i className="ri-search-line text-gray-500 text-sm"></i>
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    placeholder="Search AI tools…"
                    className="flex-1 min-w-0 px-3 py-2.5 bg-transparent text-sm text-gray-200 placeholder-gray-500 focus:outline-none cursor-text"
                  />
                </div>
              </form>
            </div>

            {navLinks.map((link) => {
              if (link.type === 'scroll') {
                return (
                  <button
                    key={link.label}
                    onClick={() => {
                      const sectionId = link.href.substring(2);
                      const section = document.getElementById(sectionId);
                      if (section) section.scrollIntoView({ behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 text-sm font-medium transition-all duration-200 cursor-pointer rounded-lg"
                  >
                    {link.label}
                  </button>
                );
              }
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 text-sm font-medium transition-all duration-200 cursor-pointer rounded-lg"
                >
                  {link.label}
                </Link>
              );
            })}

            <Link
              to="/compare"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-teal-400/80 hover:text-teal-300 hover:bg-teal-500/8 text-sm font-medium transition-all duration-200 cursor-pointer rounded-lg"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-scales-3-line text-sm"></i>
              </span>
              Compare
            </Link>

            <Link
              to="/submit-tool"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-teal-300 hover:text-white bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-circle-line text-sm"></i>
              </span>
              Submit Tool
            </Link>
          </div>
        </div>

      </div>
    </nav>
  );
}
